import { Text, useComponentProps, withDatasourceCheck } from '@sitecore-jss/sitecore-jss-nextjs';
import { ComponentProps } from 'lib/component-props';
import { GetStaticComponentProps } from '@sitecore-jss/sitecore-jss-nextjs/types';
import useHelpers from 'hooks/helpers';
import ArticleSeriesCard from 'components/Tiles/ArticleSeriesCard';
import { ArticleSeriesType } from 'model/Article';
import { useSitecoreContext } from 'hooks/useSitecoreContext';
import { defaultLanguage } from 'temp/config';
import { articleService } from 'lib/services/ArticleService';

type ArticleSeriesCollectionProps = ComponentProps & {
  fields: {
    description: {
      value: string;
    };
    series: {
      value: Array<ArticleSeriesType>;
    };
    title: {
      value: string;
    };
  };
};

const ArticleSeriesCollection = ({ fields, rendering }: ArticleSeriesCollectionProps) => {
  const { anchorId } = useHelpers(rendering);
  const { sitecoreContext } = useSitecoreContext();
  const articleSeriesCount = useComponentProps<Record<string, number>>(rendering.uid);

  const getSeries =
    fields.series?.value?.filter((series) => series.availableArticlesCount !== 0) || [];
  const articlesExist = getSeries.length || sitecoreContext.pageEditing;
  return (
    articlesExist && (
      <section
        id={anchorId}
        data-e2e-component-uid={rendering.uid}
        className="default-section-margin short-section container"
      >
        <Text tag="h2" className="h2 mb-3" field={fields.title} />
        <Text
          tag="p"
          className="body-large text-grey mb-3 mb-md-4"
          field={fields.description}
          styles={{ fontWeight: 500 }}
        />
        <div className="row d-flex flex-wrap">
          {getSeries.map((article) => (
            <ArticleSeriesCard
              key={article.id}
              count={articleSeriesCount?.[article.id] || 0}
              fields={article}
              className="col-12 col-md-6 col-lg-4 mb-3 mb-md-4"
            />
          ))}
        </div>
      </section>
    )
  );
};

export const getStaticProps: GetStaticComponentProps = async (rendering, layoutData) => {
  const fields = rendering.fields as any;
  const siteId = layoutData.sitecore.context?.siteRoot;
  const language = layoutData.sitecore.context.language ?? defaultLanguage;
  const seriesCollection: ArticleSeriesType[] = fields.series?.value || [];

  const articleNumbers = seriesCollection.map(async (s) => {
    return [s.id, await articleService.getArticleCount(language, siteId, s.id)] as [string, number];
  });

  const articleCountsAwaited = await Promise.all(articleNumbers);

  return Object.fromEntries(articleCountsAwaited);
};

export default withDatasourceCheck()<ArticleSeriesCollectionProps>(ArticleSeriesCollection);
